/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\providers\GoogleVoiceAudioControls.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/


import { RotateCcw } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import { useI18n } from '../../../../hooks/useI18n';

interface AudioControlsProps {
    speakingRate: number;
    pitch: number;
    volumeGainDb: number;
    onUpdate: (patch: { speakingRate?: number; pitch?: number; volumeGainDb?: number }) => void;
}

interface SliderFieldProps {
    label: React.ReactNode;
    value: number;
    min: number;
    max: number;
    step: number;
    defaultValue: number;
    lowLabel: string;
    highLabel: string;
    displayValue: string;
    onChange: (v: number) => void;
    onReset: () => void;
    resetKey: string;
}

function SliderField({ label, value, min, max, step, defaultValue, lowLabel, highLabel, displayValue, onChange, onReset }: SliderFieldProps) {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">{label} {displayValue}</Label>
                {value !== defaultValue && (
                    <button
                        type="button"
                        onClick={onReset}
                        className="p-1 rounded transition-colors text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                        title="Reset"
                    >
                        <RotateCcw className="h-3 w-3" />
                    </button>
                )}
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
                <span>{lowLabel}</span>
                <span>{highLabel}</span>
            </div>
        </div>
    );
}

export function GoogleVoiceAudioControls({ speakingRate, pitch, volumeGainDb, onUpdate }: AudioControlsProps) {
    const { t } = useI18n();

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SliderField
                label={<><span>?</span> {t('aiProfiles.googleTts.speed') || 'Velocidad'}:</>}
                value={speakingRate}
                min={0.25} max={4.0} step={0.1} defaultValue={1.0}
                displayValue={`${speakingRate.toFixed(1)}x`}
                lowLabel={t('aiProfiles.googleTts.slow') || 'Lento'}
                highLabel={t('aiProfiles.googleTts.fast') || 'R�pido'}
                onChange={(v) => onUpdate({ speakingRate: v })}
                onReset={() => onUpdate({ speakingRate: 1.0 })}
                resetKey="speakingRate"
            />
            <SliderField
                label={<><span>??</span> {t('aiProfiles.googleTts.pitch') || 'Tono'}:</>}
                value={pitch}
                min={-20} max={20} step={0.5} defaultValue={0}
                displayValue={`${pitch > 0 ? '+' : ''}${pitch.toFixed(1)}st`}
                lowLabel={t('aiProfiles.googleTts.low') || 'Grave'}
                highLabel={t('aiProfiles.googleTts.high') || 'Agudo'}
                onChange={(v) => onUpdate({ pitch: v })}
                onReset={() => onUpdate({ pitch: 0 })}
                resetKey="pitch"
            />
            <SliderField
                label={<><span>??</span> {t('aiProfiles.googleTts.volume') || 'Volumen'}:</>}
                value={volumeGainDb}
                min={-10} max={10} step={1} defaultValue={0}
                displayValue={`${volumeGainDb > 0 ? '+' : ''}${volumeGainDb}dB`}
                lowLabel={t('aiProfiles.googleTts.quiet') || 'Bajo'}
                highLabel={t('aiProfiles.googleTts.loud') || 'Alto'}
                onChange={(v) => onUpdate({ volumeGainDb: v })}
                onReset={() => onUpdate({ volumeGainDb: 0 })}
                resetKey="volumeGainDb"
            />
        </div>
    );
}
